/**
 * MIDI (Web MIDI): message parsing, learnable bindings (CC → parameter with soft takeover,
 * notes/program changes → actions) and MIDI clock → tempo. Pure logic; the browser plumbing is in
 * MidiController.
 */

export type MidiMessage =
  | { type: 'cc'; channel: number; number: number; value: number }
  | { type: 'noteon'; channel: number; number: number; value: number }
  | { type: 'noteoff'; channel: number; number: number }
  | { type: 'program'; channel: number; number: number }
  | { type: 'clock' }
  | { type: 'start' }
  | { type: 'continue' }
  | { type: 'stop' };

export function parseMidi(data: ArrayLike<number>): MidiMessage | null {
  const status = data[0];
  if (status === undefined) return null;
  if (status === 0xf8) return { type: 'clock' };
  if (status === 0xfa) return { type: 'start' };
  if (status === 0xfb) return { type: 'continue' };
  if (status === 0xfc) return { type: 'stop' };
  const kind = status & 0xf0;
  const channel = (status & 0x0f) + 1;
  const d1 = data[1] ?? 0;
  const d2 = data[2] ?? 0;
  switch (kind) {
    case 0xb0:
      return { type: 'cc', channel, number: d1, value: d2 };
    case 0x90:
      return d2 === 0
        ? { type: 'noteoff', channel, number: d1 }
        : { type: 'noteon', channel, number: d1, value: d2 };
    case 0x80:
      return { type: 'noteoff', channel, number: d1 };
    case 0xc0:
      return { type: 'program', channel, number: d1 };
  }
  return null;
}

export interface MidiTrigger {
  kind: 'cc' | 'note' | 'program';
  channel: number;
  number: number;
}

export type MidiTarget =
  | { kind: 'param'; path: string; label: string }
  | { kind: 'action'; action: string; arg?: number; label: string };

export interface MidiBinding {
  id: string;
  trigger: MidiTrigger;
  target: MidiTarget;
}

export function triggerOf(msg: MidiMessage): MidiTrigger | null {
  if (msg.type === 'cc') return { kind: 'cc', channel: msg.channel, number: msg.number };
  if (msg.type === 'noteon') return { kind: 'note', channel: msg.channel, number: msg.number };
  if (msg.type === 'program') return { kind: 'program', channel: msg.channel, number: msg.number };
  return null;
}

export function sameTrigger(a: MidiTrigger, b: MidiTrigger): boolean {
  return a.kind === b.kind && a.channel === b.channel && a.number === b.number;
}

export function describeTrigger(t: MidiTrigger): string {
  return t.kind === 'cc'
    ? `CC ${t.number} · ch ${t.channel}`
    : t.kind === 'note'
      ? `Note ${t.number} · ch ${t.channel}`
      : `Program ${t.number} · ch ${t.channel}`;
}

/**
 * Soft takeover: a physical knob only takes over a parameter once it passes through the
 * parameter's current value, so turning it never makes the value jump.
 */
export class SoftTakeover {
  private engaged = new Map<string, boolean>();
  private last = new Map<string, number>();

  /** Returns true when `incoming` (0..1) should be applied to a parameter currently at `current` (0..1). */
  accept(key: string, incoming: number, current: number): boolean {
    if (this.engaged.get(key)) {
      this.last.set(key, incoming);
      return true;
    }
    const prev = this.last.get(key);
    this.last.set(key, incoming);
    const tolerance = 0.03;
    const crossed =
      Math.abs(incoming - current) <= tolerance ||
      (prev !== undefined && (prev - current) * (incoming - current) <= 0);
    if (crossed) this.engaged.set(key, true);
    return crossed;
  }

  /** The parameter changed elsewhere (preset recall, UI): require pickup again. */
  release(key?: string): void {
    if (key === undefined) this.engaged.clear();
    else this.engaged.delete(key);
  }
}

/** MIDI clock (24 pulses per quarter note) → beats with a smoothed period. */
export class MidiClock {
  private ticks = 0;
  private lastTick = -1;
  private periodMs = 500;
  private haveTempo = false;
  running = false;

  /** Feed one clock tick at `timeMs`. Returns a beat when one completes. */
  tick(timeMs: number): { beatTimeMs: number; bpm: number } | null {
    if (this.lastTick >= 0) {
      const interval = timeMs - this.lastTick;
      if (interval > 0 && interval < 200) {
        const beatMs = interval * 24;
        // Heavy smoothing: browser MIDI timestamps jitter by a millisecond or two per tick.
        this.periodMs = this.haveTempo ? this.periodMs + 0.08 * (beatMs - this.periodMs) : beatMs;
        this.haveTempo = true;
      }
    }
    this.lastTick = timeMs;
    this.ticks = (this.ticks + 1) % 24;
    if (this.ticks === 1 && this.haveTempo)
      return { beatTimeMs: timeMs, bpm: 60000 / this.periodMs };
    return null;
  }

  start(): void {
    this.ticks = 0;
    this.running = true;
  }

  stop(): void {
    this.running = false;
  }

  get bpm(): number {
    return 60000 / this.periodMs;
  }
}
